import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  Copy,
  CheckCircle2,
  ChevronLeft,
} from "lucide-react";
import { api } from "@/lib/api";

type Me = {
  manualScraper: { id: string; email: string; name: string | null };
};

type TokenResponse = {
  token: string;
  expiresAt: string;
  ttlDays: number;
};

export default function ManualScraperExtension() {
  const navigate = useNavigate();
  const [meLoading, setMeLoading] = useState(true);
  const [me, setMe] = useState<Me["manualScraper"] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [token, setToken] = useState<TokenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<Me>("/manual-scraper/me").then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data?.manualScraper) {
        navigate("/manual-scrape/login", { replace: true });
        return;
      }
      setMe(res.data.manualScraper);
      setMeLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function generate() {
    setError(null);
    setGenerating(true);
    const res = await api<TokenResponse>("/manual-scraper/extension/token", {
      method: "POST",
    });
    setGenerating(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't generate a token.");
      return;
    }
    setToken(res.data);
  }

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  if (meLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-12">
      <Link
        to="/manual-scrape"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ChevronLeft className="h-4 w-4" /> Back to portal
      </Link>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
        Reputely Capture extension
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        Signed in as <strong>{me?.email}</strong>. Generate a bearer token
        below, then paste it into the extension&rsquo;s Settings page.
      </p>

      <div className="mt-8 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          Setup
        </h2>
        <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-2">
          <li>
            Install the <em>Reputely Capture</em> Chrome extension (Load
            unpacked from <code>apps/extension/</code>).
          </li>
          <li>Click the extension icon → <strong>Settings</strong>.</li>
          <li>
            Click <strong>Generate token</strong> below, copy it, paste into
            the extension Settings <em>Bearer token</em> field, click{" "}
            <strong>Save</strong>.
          </li>
          <li>
            Claim a job in the <Link to="/manual-scrape" className="text-slate-900 underline">portal</Link>,
            open the ASIN on Amazon, hover any review → <strong>Save to
            Reputely</strong>.
          </li>
        </ol>

        <div className="border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {generating && <Loader2 className="h-4 w-4 animate-spin" />}
            {generating ? "Generating…" : "Generate token"}
          </button>
          {error && (
            <p className="mt-3 flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" /> {error}
            </p>
          )}
        </div>

        {token && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="flex items-center gap-2 font-semibold text-amber-900">
              <CheckCircle2 className="h-4 w-4" /> New token — copy now, it
              won&rsquo;t be shown again
            </p>
            <pre className="mt-3 overflow-x-auto rounded bg-white p-3 font-mono text-xs text-slate-900">
              {token.token}
            </pre>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy token
                  </>
                )}
              </button>
              <span className="text-xs text-amber-900">
                Expires {new Date(token.expiresAt).toLocaleString()} (
                {token.ttlDays} days). Generate a new one to rotate.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
